import { useEffect, useMemo, useState } from 'react';

import {
  CANDIDATE_LEVELS,
  DEFAULT_LIMIT,
  fetchLocalCandidates,
  fetchStateAuthority,
} from '../utils/localCandidatesApi';

/**
 * Candidates for state, county and city contests, from api2.govglance.org.
 *
 * A sibling to useCandidates rather than an extension of it, because almost
 * nothing is shared: that hook talks to api.govglance.org with a bearer token
 * and is built entirely around the FEC candidate ID -- parsing office and state
 * back out of it, deduplicating on it, and routing to a detail page keyed on
 * it. Below federal level there is no FEC ID at all, so every one of those
 * mechanisms has nothing to work with.
 *
 * The reading and the request now live in utils/localCandidatesApi, so this and
 * useLocalCandidate normalize a row identically. A candidate should not read
 * differently depending on which screen opened them.
 *
 * @param {Object}  params
 * @param {string}  params.stateAbbr - Two letter code. Required.
 * @param {string}  params.level     - 'state' | 'county' | 'city' | 'local'
 * @param {string}  [params.county]  - County name, with or without the noun.
 * @param {number}  [params.year]
 * @param {number}  [params.limit]
 */

const useLocalCandidates = ({
  stateAbbr,
  level = 'county',
  county = '',
  year,
  limit = DEFAULT_LIMIT,
} = {}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [authority, setAuthority] = useState(null);

  useEffect(() => {
    if (!stateAbbr || !CANDIDATE_LEVELS.includes(level)) {
      setCandidates([]);
      setAuthority(null);
      setTruncated(false);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setAuthority(null);

        const result = await fetchLocalCandidates(
          { stateAbbr, level, county, year, limit },
          { signal: controller.signal }
        );

        if (controller.signal.aborted) return;

        setCandidates(result.candidates);
        setTruncated(result.truncated);

        // Only when there is nothing, so it costs nothing in the normal case.
        if (!result.candidates.length) {
          const fallback = await fetchStateAuthority(stateAbbr, {
            signal: controller.signal,
          });
          if (!controller.signal.aborted) setAuthority(fallback);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setCandidates([]);
        setTruncated(false);
        setError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [stateAbbr, level, county, year, limit]);

  // The counties actually present in the answer, which is a truer list than
  // every county in the state: it names only the ones with a published field.
  // Indiana school boards and Colorado's RTD board arrive with no county at
  // all, so they are absent here by design rather than filed under a guess.
  const counties = useMemo(() => {
    const seen = new Set();
    candidates.forEach((candidate) => {
      if (candidate.county) seen.add(candidate.county);
    });
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [candidates]);

  return { candidates, counties, loading, error, truncated, authority };
};

export default useLocalCandidates;